// src/client/renderer/character/outline-binding.ts
//
// Outline binding system for SmashParty character rig (Plan 13, Task 5.1).
//
// Consumes Plan 14a's applyOutline(mesh, opts?) contract at
// src/client/renderer/shaders/outline.ts:37.
// Do NOT re-implement the outline shader — delegate to applyOutline.
//
// Outline siblings attach next to each render mesh. Skinned body meshes receive
// bound SkinnedMesh outline siblings, so the outline follows animation bones.
//
// Lifecycle: caller-managed via the returned { dispose() } handle.
// NOT slot-based (no _outlineHandle on CharacterRig) per plan contract.

import * as THREE from 'three';
import { applyOutline } from '../shaders/outline';
import type { CharacterRig, ColorId } from './types';
import { COLOR_ID_TO_HEX } from './types';
import { getRigRenderMeshes } from './mesh-targets';

/**
 * Bind a player-colored outline to every render mesh in the rig.
 *
 * Delegates to Plan 14a's `applyOutline(mesh, opts)` which attaches an
 * inverted-hull sibling mesh to each mesh parent. For rigged bodies this
 * includes the SkinnedMesh sibling path in `applyOutline`.
 *
 * @returns A dispose handle whose `dispose()` detaches and disposes the outline.
 *   The caller is responsible for tracking and calling this handle. It is NOT
 *   wired into `rig.dispose()` (caller-managed per plan contract).
 */
export function bindCharacterOutline(args: {
  rig: CharacterRig;
  colorId: ColorId;
  thickness?: number;
}): { dispose(): void } {
  const { rig, colorId, thickness } = args;

  const handles = getRigRenderMeshes(rig).map((mesh) =>
    applyOutline(mesh, {
      color: COLOR_ID_TO_HEX[colorId],
      thickness: thickness ?? 0.02,
    }),
  );

  let disposed = false;
  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const handle of handles) handle.dispose();
    },
  };
}

/**
 * Enable shadow casting on every mesh (and sprite) in the rig.
 *
 * Pure traversal helper — walks rig.root and sets `castShadow = true` on
 * every Object3D child (Mesh, Sprite, etc.). Call once after the rig is
 * fully assembled (body + head + eyes + cape all attached).
 */
export function enableCharacterShadows(rig: CharacterRig): void {
  rig.root.traverse((child: THREE.Object3D) => {
    if (child.userData.characterEyes === true) {
      child.castShadow = false;
      return;
    }
    child.castShadow = true;
  });
}
